import { CHARACTER_LENGTH } from "@constants/index";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { CustomDatePicker } from "@components/Forms";
import { useNavigate } from "react-router-dom";
import TableWithPagination from "@components/Tables/TableWithPagination";
import Breadcrumb from "@components/UI/Breadcrumb";
import CustomSelect from "@components/UI/CustomSelect";
import FileUploader from "@components/UI/FileUploader";
import LicenseSelectionModal from "@components/UI/LicenseSelectionModal";
import MemberSelectionModal from "@components/UI/MemberSelectionModal";
import PreviewSelectionModal from "@components/UI/PreviewSelectionModal";
import { useAddProjectForm } from "@hooks/useAddProjectForm";
import { useTheme } from "@hooks/useTheme";
import { translateDepartment } from "@utils/common";
import { OutlineButton, PrimaryButton } from "@components/Buttons";

const AddProject = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const {
    assetClassificationsList,
    canAddProjectMemberPermission,
    creatorList,
    creatorOptions,
    departmentList,
    distributionScopeList,
    formData,
    handleDeleteMember,
    handleInputChange,
    handleLicenseSelect,
    handleMemberSelect,
    handlePreviewSelect,
    handleProjectManagerChange,
    handleSave,
    isFormValid,
    isPreviewEnabled,
    isPreviewModalOpen,
    licensePolicyList,
    loading,
    modalSelectLicenseOpen,
    modalSelectMemberOpen,
    projectManagerList,
    myProductsList,
    setIsPreviewModalOpen,
    setModalSelectLicenseOpen,
    setModalSelectMemberOpen,
  } = useAddProjectForm({
    onCreateCompleted: () => navigate("/projects"),
  });

  const breadcrumbItems = [
    { label: t("project.project-list-title"), href: "/projects" },
    { label: t("project.add-new-project"), href: "/projects/new" },
  ];

  const handleCancel = () => {
    navigate("/projects");
  };

  // Table config for selected members
  const memberTableColumns = ["userId", "userName", "email"];
  const memberTableColumnConfig = {
    userId: {
      style: { width: "20%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.user-id", "USER ID"),
    },
    userName: {
      style: { width: "35%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.user-name", "USER NAME"),
      customRender: (value, row) => (
        <div className="flex items-center space-x-3">
          <span>{row.userName}</span>
        </div>
      ),
    },
    email: {
      style: { width: "40%", textAlign: "left", padding: "0.5rem" },
      headerRender: () => t("common.email", "EMAIL"),
    },
  };


  return (
    <div data-testid="add-project-page">
      {/* Header */}
      <div className={`${colors.bgSecondary} ${colors.borderPrimary}`} data-testid="add-project-header">
        <div className="flex items-center justify-between">
          <div className="items-center space-x-4">
            <Breadcrumb items={breadcrumbItems} data-testid="add-project-breadcrumb" />
            <h1 className={`${colors.textPrimary} text-2xl font-bold uppercase`} data-testid="add-project-title">
              {t("project.add-new-project")}
            </h1>
          </div>
          <div className="flex items-center space-x-3" data-testid="add-project-header-actions">
            <OutlineButton data-testid="add-project-cancel-button" onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
            <PrimaryButton data-testid="add-project-save-button" onClick={handleSave} disabled={!isFormValid() || loading}>
              {t("common.save-button")}
            </PrimaryButton>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`mt-5 p-6 ${colors.bgPrimary} rounded-2xl`} data-testid="add-project-form">
        <div className={`mx-auto max-w-[55rem]`}>
          <div>
            {/* Project Information Section */}
            <div className="mb-6" data-testid="add-project-project-information-section">
              <h2 className={`${colors.textPrimary} text-xl font-semibold`} data-testid="add-project-project-information-title">
                {t("project.project-information-title")}
              </h2>
              <p className={`mb-2 ${colors.textSubTitle} text-sm font-normal`}>
                {t("project.required-fields-note")} <span className={colors.textRequired}>*</span>
              </p>

              <div className="grid grid-cols-3 gap-6">
                {/* Project Name */}
                <div className="col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.project-name")} <span className={colors.textRequired}>*</span>
                  </label>
                  <input
                    type="text"
                    data-testid="add-project-project-name-input"
                    placeholder={t("project.project-name-placeholder")}
                    value={formData.projectName}
                    onChange={(e) => handleInputChange("projectName", e.target.value)}
                    title={formData.projectName}
                    className={`h-10 w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} transition-colors duration-300`}
                    maxLength={1024}
                  />
                </div>

                {/* Project Manager */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.project-manager")}
                  </label>
                  <CustomSelect
                    options={projectManagerList || []}
                    value={projectManagerList?.find((opt) => opt.value === formData.projectManagerId) || null}
                    onChange={handleProjectManagerChange}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    isClearable={true}
                    displayFormatLabelWithID={true}
                    className="w-full text-sm font-medium"
                  />
                </div>

                {/* Product Name */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.product-name")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomSelect
                    data-testid="add-project-product-select"
                    options={myProductsList || []}
                    value={myProductsList?.find((opt) => opt.value === formData.productSeq) || null}
                    onChange={(selectedOption) => handleInputChange("productSeq", selectedOption?.value || "")}
                    placeholder={t("common.please-select")}
                    isSearchable={true}
                    isClearable={true}
                    className="w-full text-sm font-medium"
                  />
                </div>

                {/* Completion Request Date */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.completion-request-date")} <span className={colors.textRequired}>*</span>
                  </label>
                  <CustomDatePicker
                    data-testid="add-project-completion-request-date-picker"
                    selected={formData.completionRequestDate}
                    onChange={(date) => handleInputChange("completionRequestDate", date)}
                    placeholderText={t("project.select-date-placeholder")}
                    title={formData.completionRequestDate ? formData.completionRequestDate.toLocaleDateString() : ""}
                    className={`h-10 w-full appearance-none rounded-lg border p-3 text-sm font-medium focus:outline-none truncate ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    dateFormat="yyyy-MM-dd"
                    minDate={new Date()}
                  />
                </div>

                {/* Asset Classification */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.asset-classification")}
                  </label>
                  <CustomSelect
                    data-testid="add-project-asset-classification-select"
                    options={assetClassificationsList || []}
                    value={assetClassificationsList?.find((opt) => opt.value === formData.assetClassification) || null}
                    onChange={(opt) => handleInputChange("assetClassification", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className="w-full text-sm font-medium"
                  />
                </div>

                {/* Distribution Scope */}
                <div>
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.distribution")}
                  </label>
                  <CustomSelect
                    data-testid="add-project-distribution-scope-select"
                    options={distributionScopeList || []}
                    value={distributionScopeList?.find((opt) => opt.value === formData.distributionScope) || null}
                    onChange={(opt) => handleInputChange("distributionScope", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className={`w-full text-sm font-medium ${colors.textPrimary}`}
                  />
                </div>

                {/* License Policy */}
                <div className="col-span-2">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.license-policy")}
                  </label>
                  <CustomSelect
                    data-testid="add-project-license-policy-select"
                    options={licensePolicyList || []}
                    value={licensePolicyList?.find((opt) => opt.value === formData.licensePolicy) || null}
                    onChange={(opt) => handleInputChange("licensePolicy", opt?.value || null)}
                    placeholder={t("common.please-select")}
                    isSearchable={false}
                    clearable={false}
                    className="w-full text-sm font-medium"
                  />
                </div>

                {/* License */}
                <div className="relative">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("project.clarity.license")}
                  </label>
                  <div>
                    <input
                      type="text"
                      data-testid="add-project-license-input"
                      placeholder={t("project.select-license-placeholder")}
                      value={formData.selectedLicense?.licenseName || ""}
                      readOnly
                      onClick={() => setModalSelectLicenseOpen(true)}
                      title={formData.selectedLicense?.licenseName || ""}
                      className={`h-10 w-full cursor-pointer rounded-lg border truncate ps-3 pr-10 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                    />
                    <MagnifyingGlassIcon
                      className={`pointer-events-none absolute top-10 right-4 h-4 w-4 ${colors.textActive}`}
                    />
                  </div>
                </div>

                {/* Preview List */}
                {isPreviewEnabled && (
                  <div className="col-span-2 relative">
                    <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                      {t("verification-comparison.preview-list")}
                    </label>
                    <div>
                      <input
                        type="text"
                        data-testid="add-project-preview-input"
                        placeholder={t("project.select-preview-title")}
                        value={formData.selectedProjectPreview?.projectPreviewName || ""}
                        readOnly
                        onClick={() => setIsPreviewModalOpen(true)}
                        className={`h-10 w-full cursor-pointer rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.borderPrimary} ${colors.bgInput} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                      />
                      <MagnifyingGlassIcon
                        className={`pointer-events-none absolute top-10 right-4 h-4 w-4 ${colors.textActive}`}
                      />
                    </div>
                  </div>
                )}

                {/* Remark */}
                <div className="col-span-3">
                  <label className={`mb-2 block text-sm font-semibold ${colors.textSecondary}`}>
                    {t("common.remark")}
                  </label>
                  <textarea
                    rows={4}
                    data-testid="add-project-remark-textarea"
                    placeholder={t("project.remark-placeholder")}
                    value={formData.remark}
                    onChange={(e) => handleInputChange("remark", e.target.value)}
                    title={formData.remark}
                    maxLength={CHARACTER_LENGTH.REMARK}
                    className={`w-full rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary} resize-none`}
                  />
                  <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="add-project-remark-char-count">
                    {(formData.remark || "").length}/{CHARACTER_LENGTH.REMARK}
                  </p>
                </div>
              </div>
            </div>

            {/* Documents Section */}
            <div className={`mb-6`} data-testid="add-project-documents-section">
              <h2 className={`${colors.textPrimary} mb-2 font-bold`} data-testid="add-project-documents-title">
                {t("project.documents")}
              </h2>
              <FileUploader
                data-testid="add-project-file-uploader"
                onFilesChange={(files) => handleInputChange("selectedFiles", files)}
                selectedFiles={formData.selectedFiles}
              />
            </div>

            {/* Members Section */}
            {canAddProjectMemberPermission && (
            <div className={colors.borderPrimary} data-testid="add-project-members-section">
              <h2 className={`${colors.textPrimary} mb-2 font-bold uppercase`} data-testid="add-project-members-title">{t("project.members")}</h2>

              {/* Display selected members */}
              {formData.selectedMembers.length > 0 && (
                <div className="mb-4" data-testid="add-project-members-table">
                  <TableWithPagination
                    dataTestId="add-project-members-table-grid"
                    columns={memberTableColumns}
                    columnConfig={memberTableColumnConfig}
                    data={formData.selectedMembers}
                    currentPage={1}
                    setCurrentPage={() => {}} // No pagination needed
                    loading={false}
                    rowKey="userId"
                    totalPages={1}
                    totalData={formData.selectedMembers.length}
                    maxRowsPerPage={formData.selectedMembers.length}
                    hasCheckbox={false}
                    hasDeleteColumn={true}
                    onDeleteRow={handleDeleteMember}
                    showPagination={false}
                    onItemsPerPageChange={() => {}} // Not needed
                  />
                </div>
              )}

              {/* Add member button */}
              <div
                className={`${colors.bgDropFiles} border-1 border-dashed ${colors.borderActiveDashed} rounded-lg p-1 text-center`}
                data-testid="add-project-add-member-zone"
              >
                <div className="flex justify-center not-first-of-type:items-center">
                  <div className="flex items-center">
                    <button
                      onClick={() => setModalSelectMemberOpen(true)}
                      data-testid="add-project-add-member-button"
                      className={`px-4 py-2 text-sm font-semibold ${colors.textActive} rounded-lg transition-all duration-1000 ease-in-out`}
                    >
                      <span className="text-sm font-semibold">
                        {formData.selectedMembers.length > 0 ? t("project.add-more-members") : t("project.add-member")}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Preview Selection Modal */}
      <PreviewSelectionModal
        data-testid="add-project-preview-selection-modal"
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        onSelect={handlePreviewSelect}
        selectedPreview={formData.selectedProjectPreview}
        creatorList={creatorList}
      />

      {/* License Selection Modal */}
      <LicenseSelectionModal
        data-testid="add-project-license-selection-modal"
        isOpen={modalSelectLicenseOpen}
        onClose={() => setModalSelectLicenseOpen(false)}
        onSelect={handleLicenseSelect}
        selectedLicense={formData.selectedLicense}
      />

      {canAddProjectMemberPermission && (
        <MemberSelectionModal
          data-testid="add-project-member-selection-modal"
          isOpen={modalSelectMemberOpen}
          onClose={() => setModalSelectMemberOpen(false)}
          onSelect={handleMemberSelect}
          selectedMember={formData.selectedMembers}
          creatorList={creatorOptions}
          departmentList={departmentList}
          projectManagerId={formData.projectManagerId || null}
        />
      )}
    </div>
  );
};

export default AddProject;
